import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import {
  GET_NOTIFICATION_FEED,
  GET_NOTIFICATION_UNREAD_COUNT,
  MARK_ALL_NOTIFICATION_READ,
  MARK_NOTIFICATION_READ,
} from "../constant/CommonApiUrl";
import {
  isFeatureDisabled,
  mergeNotifications,
  normalizeNotification,
} from "../helper/notifications";

export const NOTIFICATION_PAGE_SIZE = 20;

// The bell is deliberately cross-vessel: UnreadCount takes no vessel parameter, so
// scoping the list to one vessel would leave the badge counting rows the list never
// shows. within_days=0 is the same argument in time — the badge has no window, so a
// windowed list would count unread rows the user can no longer click.
const buildFeedQuery = ({ page, pageSize }) =>
  new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
    within_days: "0",
  }).toString();

// These calls deliberately bypass the generic getAllData thunk: ValidateResponse
// toasts an error whenever status is false, which would fire on every page load
// for a tenant that has the feed switched off.
export const fetchNotificationFeed = createAsyncThunk(
  "notification/fetchFeed",
  async ({ page = 1, pageSize = NOTIFICATION_PAGE_SIZE } = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(
        `${GET_NOTIFICATION_FEED}?${buildFeedQuery({ page, pageSize })}`,
      );
      return { ...response.data.result, requestedPage: page };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchUnreadCount = createAsyncThunk(
  "notification/fetchUnreadCount",
  async (_arg, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_NOTIFICATION_UNREAD_COUNT);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const markNotificationsRead = createAsyncThunk(
  "notification/markRead",
  async ({ ids }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(MARK_NOTIFICATION_READ, {
        ids,
        vesselId: null,
      });
      return { ...response.data.result, ids };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const markAllNotificationsRead = createAsyncThunk(
  "notification/markAllRead",
  async (_arg, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(MARK_ALL_NOTIFICATION_READ);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const initialState = {
  rows: [],
  unreadCount: 0,
  page: 1,
  totalPage: 0,
  totalData: 0,
  loadStatus: "idle", // idle | loading | loaded | error
  writeFailed: false, // a mark-read / mark-all-read call did not go through
  gate: "unknown", // unknown | enabled | disabled
};

// Every response tells us whether the feed exists for this tenant.
const applyGate = (state, payload) => {
  if (isFeatureDisabled(payload)) {
    state.gate = "disabled";
    return false;
  }
  state.gate = "enabled";
  return true;
};

const stampRead = (state, ids) => {
  const stamped = new Set(ids.map(Number));
  const now = new Date().toISOString();
  state.rows = state.rows.map((row) =>
    stamped.has(row.id) ? { ...row, readAtUtc: row.readAtUtc ?? now } : row,
  );
};

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    // The socket payload is a feed row, so it can go straight into the store.
    pushNotification: (state, { payload }) => {
      const row = normalizeNotification(payload);
      const isNew = !state.rows.some((existing) => existing.id === row.id);

      state.rows = mergeNotifications(state.rows, [row]);
      if (isNew && !row.readAtUtc) state.unreadCount += 1;
    },
    // Keeps the gate: whether the feed exists is a tenant property, so re-deciding
    // it every time the store is cleared would flicker the bell.
    resetNotifications: (state) => ({ ...initialState, gate: state.gate }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationFeed.pending, (state) => {
        state.loadStatus = "loading";
      })
      .addCase(fetchNotificationFeed.fulfilled, (state, { payload }) => {
        state.loadStatus = "loaded";
        if (!applyGate(state, payload)) return;

        // messageCode "204" means an empty bell, and arrives with data: null.
        const rows = Array.isArray(payload?.data) ? payload.data : [];

        state.rows =
          payload.requestedPage > 1
            ? mergeNotifications(state.rows, rows)
            : mergeNotifications([], rows);
        state.page = payload.requestedPage;
        state.totalPage = payload.totalPage || 0;
        state.totalData = payload.totalData || 0;
      })
      .addCase(fetchNotificationFeed.rejected, (state) => {
        state.loadStatus = "error";
      })
      .addCase(fetchUnreadCount.fulfilled, (state, { payload }) => {
        if (!applyGate(state, payload)) return;
        // The badge is never derived from rows.length — the feed is paged.
        state.unreadCount = payload?.data?.unread_count ?? state.unreadCount;
      })
      .addCase(markNotificationsRead.pending, (state) => {
        state.writeFailed = false;
      })
      .addCase(markNotificationsRead.fulfilled, (state, { payload }) => {
        if (!applyGate(state, payload)) return;
        // rows_updated: 0 (already read, or not the caller's row) is a no-op, not an error.
        state.unreadCount = payload?.data?.unread_count ?? state.unreadCount;
        stampRead(state, payload.ids);
      })
      .addCase(markAllNotificationsRead.pending, (state) => {
        state.writeFailed = false;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state, { payload }) => {
        if (!applyGate(state, payload)) return;
        state.unreadCount = payload?.data?.unread_count ?? 0;
        stampRead(
          state,
          state.rows.map((row) => row.id),
        );
      })
      // A stale badge is recoverable on the next fetch, so a failed count is not
      // worth surfacing — but it must not be mistaken for a zero.
      .addCase(fetchUnreadCount.rejected, () => {})
      .addCase(markNotificationsRead.rejected, (state) => {
        state.writeFailed = true;
      })
      .addCase(markAllNotificationsRead.rejected, (state) => {
        state.writeFailed = true;
      });
  },
});

export const { pushNotification, resetNotifications } =
  notificationSlice.actions;
export default notificationSlice.reducer;
